import React from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { cn } from '../../../../lib/utils';

interface ControlledInputProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  hint?: string;
}

export const ControlledInput = <T extends FieldValues>({ 
  control, 
  name, 
  label, 
  type = 'text',
  placeholder,
  disabled = false,
  min,
  max,
  hint
}: ControlledInputProps<T>) => {
  const hintId = `${String(name)}-hint`;
  const errorId = `${String(name)}-error`;
  
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const describedBy = [
          hint ? hintId : null,
          fieldState.error ? errorId : null,
        ].filter(Boolean).join(' ') || undefined;
        
        return (
          <div className="space-y-2">
            <Label htmlFor={String(name)} className="text-sm font-medium">
              {label}
            </Label>
            <Input 
              id={String(name)} 
              {...field}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              className={cn(
                fieldState.error && "border-red-500 border-2 focus-visible:ring-red-500 focus-visible:ring-2"
              )}
              aria-invalid={!!fieldState.error}
              aria-describedby={describedBy}
            />
            {hint && !fieldState.error && (
              <span id={hintId} className="text-sm text-text-secondary">
                {hint}
              </span>
            )}
            {fieldState.error && (
              <span 
                id={errorId}
                className="text-destructive text-sm"
                role="alert"
              >
                {fieldState.error.message}
              </span>
            )}
          </div>
        );
      }}
    />
  );
};
